import React, { useState, useEffect } from "react";
import FormSectionLayout from "components/layouts/FormSectionLayout";
import HeadingText from "components/common/HeadingText/HeadingText";
import { Col, Container, Form, Row } from "reactstrap";
import Modal from "components/common/Modal/Modal";
import TextField from "components/common/TextField/TextField";
import "./AddUpdateCampaignPage.scss";
import ButtonComponent from "components/common/Button/ButtonComponent";
import SearchableSelect from "components/common/SearchableSelect/SearchableSelect";
import UploadButtonComponent from "components/common/UploadButton/UploadButtonComponent";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  acceptableFormats,
  STATUS,
  CAMPAIGN_TYPE,
  CAMPAIGN_TYPE_DROPDOWN,
} from "config/constants";
import { Can } from "config/Can";
import {
  IAddUpdateCampaignPageProps,
  ICreateCampaignRequestPayload,
  ICampaignCSVData,
  ICampaignCycle,
  IImageObject,
  IMultiSelectOption,
  ISearchSelectChange,
} from "interfaces/Index";
import LoaderComponent from "components/common/Loader/Loader";
import {
  isNumeric,
  formatDate,
  getTomorrowDate,
  calculateDateDifference,
} from "utils/lib";
import { DATE_FORMAT } from "config/constants";
import IconBackArrow from "assets/images/back_arrow.svg";
import PlaceholderImage from "assets/product_placeholder_image.png";
import XLSX from "xlsx";
import Switch from "components/common/Switch/Switch";
import { format, addHours, isWithinInterval } from "date-fns";
import MultiSelectDropDown from "components/common/MultiSelectDropDown/MultiSelectDropDown";
const AddUpdateCampaignPage: React.FunctionComponent<
  IAddUpdateCampaignPageProps
> = (props) => {
  const {
    createCampaign,
    updateCampaign,
    fetchProductBrands,
    backButtonHandler,
    isCampaignCreated,
    isCampaignUpdated,
    error,
    isError,
    isEdit,
    isLoading,
    campaign,
    productBrands,
  } = props;

  const [startDate, setStartDate] = useState(
    campaign && campaign.startDate
      ? addHours(new Date(campaign.startDate), 5).toString()
      : getTomorrowDate().toString()
  );
  const [endDate, setEndDate] = useState(
    campaign && campaign.endDate
      ? addHours(new Date(campaign.endDate), 5).toString()
      : ""
  );
  const [title, setTitle] = useState(
    campaign && campaign.title ? campaign.title : ""
  );
  const [description, setDescription] = useState(
    campaign && campaign.description ? campaign.description : ""
  );
  const [duration, setDuration] = useState(
    campaign && campaign.duration ? "" + campaign.duration : ""
  );
  const [recurrence, setRecurrence] = useState(
    campaign && campaign.recurrence ? "" + campaign.recurrence : ""
  );
  const [status, setStatus] = useState(
    campaign
      ? campaign.campaignStatus.toUpperCase() === STATUS.ACTIVE
        ? true
        : false
      : true
  );

  const [type, setType] = useState<ISearchSelectChange | undefined>(
    campaign && campaign.type
      ? CAMPAIGN_TYPE_DROPDOWN.filter((item) => item.value === campaign.type)[0]
      : props.dummyType
  );
  const [campaignImage, setCampaignImage] = useState<IImageObject>();
  const [brands, setBrands] = useState<IMultiSelectOption[]>([]);
  const [isStatusChange, setIsStatusChange] = useState(
    props.dummyIsStatusChange || false
  );
  const [notificationModal, setNotificationModal] = useState(false);
  const [csvData, setCSVData] = useState<ICampaignCSVData[]>(
    props.dummyCSVData || []
  );
  const [csvFileName, setCSVFilename] = useState(props.dummyFileName || "");
  const [modalMessage, setModalMessage] = useState("");
  const [modalTitle, setModalTitle] = useState("Are you sure?");
  const [campaignCanUpdate, setCampaignCanUpdate] = useState(false);

  const validForm =
    title.length > 0 &&
    description.length > 0 &&
    isNumeric(duration) &&
    isNumeric(recurrence) &&
    ((csvFileName.length > 0 && csvData.length > 0) || isEdit) &&
    (type && type.value === CAMPAIGN_TYPE.BRAND_VALUE
      ? (campaignImage || isEdit) && brands.length > 0
      : true) &&
    (isStatusChange || (!campaign && status));

  useEffect(() => {
    fetchProductBrands();
  }, []);

  useEffect(() => {
    if (productBrands.length > 0 && campaign && campaign.brandIds) {
      setBrands(
        productBrands.filter((brand) =>
          campaign.brandIds?.includes(brand.id)
        ) || []
      );
    }
  }, [props.productBrands]);

  useEffect(() => {
    if (isCampaignCreated || isCampaignUpdated) {
      setNotificationModal(false);
      backButtonHandler(true);
    }
  }, [props.isCampaignCreated, props.isCampaignUpdated]);

  useEffect(() => {
    if (isError) {
      setNotificationModal(false);
      alert((error! as any).errors);
    }
  }, [props.isError]);

  const handleGoBackButton = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    backButtonHandler(false);
  };

  const notificationModalState = () => {
    setNotificationModal(!notificationModal);
  };

  const handleCampaignImageChange = (file: IImageObject) => {
    setCampaignImage(file);
  };

  const handleCSVInputChange = (file: File) => {
    let csvImportData: ICampaignCSVData[] = [];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target?.result as any);
        const workbook = XLSX.read(data, { type: "array" });

        // Get 1st Excel sheet
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const dataToJson: Object[] = XLSX.utils.sheet_to_json(sheet);
        dataToJson.forEach((row: Object) => {
          csvImportData.push({
            customerId: Object.values(row)[0],
            target: Object.values(row)[1],
            rewardAmount: Object.values(row)[2],
          });
        });
        setCSVData(csvImportData);
        setCSVFilename(file.name);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleCSVRemove = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setCSVFilename("");
    setCSVData([]);
  };

  const getCampaignCycles = (
    date: Date,
    duration: number,
    recurrence: number
  ) => {
    const campaignCycles: ICampaignCycle[] = [];
    for (let i = 1; i <= recurrence; i++) {
      let startDate: Date;
      let endDate: Date;
      if (i === 1) {
        startDate = date;
      } else {
        startDate = new Date(
          date.getTime() + (i - 1) * duration * 24 * 60 * 60 * 1000
        );
      }
      endDate = new Date(date.getTime() + i * duration * 24 * 60 * 60 * 1000);
      campaignCycles.push({
        startDate,
        endDate,
      });
    }
    return campaignCycles;
  };

  const getActiveCampaignCycles = (campaignCycles: ICampaignCycle[]) => {
    const actveCycle = campaignCycles.filter((cycle) => {
      if (
        isWithinInterval(new Date(), {
          start: cycle.startDate,
          end: cycle.endDate,
        })
      ) {
        return true;
      } else {
        return false;
      }
    });

    return actveCycle;
  };

  const handleSubmit = () => {
    if (!isEdit) {
      let formData = new FormData();
      const imageData = campaignImage?.multiPartData || "";
      formData.append("image", imageData);

      const brandIds = brands.map(({ id }) => id);
      const createCampaignRequestPayload: ICreateCampaignRequestPayload = {
        campaignImage: formData,
        customers: csvData,
        brandIds,
        title,
        description,
        type: type?.value || "",
        duration: +duration,
        recurrence: +recurrence,
        startDate: new Date(startDate).toISOString(),
      };
      createCampaign(createCampaignRequestPayload);
    } else {
      const campaignId = campaign?.id || "";
      updateCampaign(campaignId);
    }
  };

  const createUpdateCampaign = () => {
    if (!isEdit) {
      setModalMessage("Are you sure you want to create this campaign?");
    } else {
      const campaignCycles = getCampaignCycles(
        new Date(startDate),
        parseInt(duration),
        parseInt(recurrence)
      );
      const activeCycles = getActiveCampaignCycles(campaignCycles);

      //If any cycle started and marked as inactive all the next campaign cycles
      if (activeCycles.length > 0) {
        // active campaign cycle is not last cycle
        if (activeCycles[0].endDate.getTime() !== new Date(endDate).getTime()) {
          setStatus(false);
          setModalMessage(
            `All campaign recurrences after ${format(
              addHours(activeCycles[0].endDate, 5),
              "LLL d"
            )} will be marked inactive.`
          );
        } else {
          setStatus(true);
          setIsStatusChange(false);
          setCampaignCanUpdate(true);
          setModalTitle("Action cannot be performed");
          setModalMessage(
            "Last campaign cycle is active and cannot be marked as inactive"
          );
        }
      } else {
        // Check if campaign is started or not
        if (calculateDateDifference(new Date(startDate)) >= 0) {
          setStatus(false);
          setModalMessage(
            `Campaign is not started yet, all campaign cycle after ${format(
              new Date(startDate),
              "LLL d"
            )} will be marked inactive.`
          );
        }
        // all campaign cycles are completed
        else {
          setStatus(true);
          setIsStatusChange(false);
          setCampaignCanUpdate(true);
          setModalTitle("Action cannot be performed");
          setModalMessage(
            "Campaign is completed and cannot be marked as inactive"
          );
        }
      }
    }
    setNotificationModal(true);
  };
  const renderNotificationModal = () => {
    return (
      <Modal
        showModal={notificationModal}
        closeModalHandler={notificationModalState}
        modalHeading={modalTitle}
        showProgressString={false}
        failedRecords=""
        modalBodyClass="notification-account"
        containerClass="notification-modal"
      >
        <div id="confirmationModal">
          {modalMessage && <p>{modalMessage}</p>}
          <Row className="button-container">
            <Col lg="6">
              <ButtonComponent
                buttonClass="button-small btn btn-primary-outline"
                buttonText="Go back"
                onClickHandler={notificationModalState}
              />
            </Col>
            <Col lg="6">
              <ButtonComponent
                buttonClass="button-small btn btn-primary verify-btn"
                buttonText={isEdit ? "Update" : "Create"}
                onClickHandler={handleSubmit}
                isDisable={campaignCanUpdate}
              />
            </Col>
          </Row>
        </div>
      </Modal>
    );
  };

  const updateEndDate = (
    updatedstartDate: string,
    updatedDuration: string,
    updatedRecurrence: string
  ) => {
    const newEndDate = new Date(updatedstartDate);
    newEndDate.setDate(
      newEndDate.getDate() +
        ((parseInt(updatedDuration) || 1) * (parseInt(updatedRecurrence) || 1) -
          1)
    );
    setEndDate(formatDate(newEndDate.toString(), DATE_FORMAT.ddMMyyyy));
  };

  const renderSubmitButton = () => (
    <FormSectionLayout subTitleText="">
      <Can I={isEdit ? "update" : "create"} a="campaign">
        <ButtonComponent
          buttonClass="large-button btn-primary"
          buttonText={isEdit ? "Update Campaign" : "Create Campaign"}
          onClickHandler={createUpdateCampaign}
          isDisable={!validForm}
        />
      </Can>
    </FormSectionLayout>
  );
  const renderCohort = () => {
    return (
      <FormSectionLayout subTitleText="Cohort">
        <div className="campaign-buttons">
          <Can I="create" a="campaign">
            <UploadButtonComponent
              id="upload-campaign-csv"
              buttonText={csvFileName ? csvFileName : "Upload .CSV"}
              fileFormat={acceptableFormats.csv}
              fileUploaded={!!csvFileName}
              handleRemoveFile={handleCSVRemove}
              handleFileChange={handleCSVInputChange}
              classes="large-button"
              isDisable={isEdit}
            />
          </Can>
        </div>
      </FormSectionLayout>
    );
  };

  const renderCampaignImage = () => {
    return (
      <FormSectionLayout subTitleText="Campaign image">
        <div className="product-image-upload-container">
          {!isEdit && campaignImage && campaignImage.base64StringUrl ? (
            <div className="product-image-container">
              <img src={campaignImage.base64StringUrl} alt="campaign" />
            </div>
          ) : isEdit && campaign && campaign.imageUrl ? (
            <div className="product-image-container">
              <img src={campaign.imageUrl} alt="campaign" />
            </div>
          ) : (
            <img src={PlaceholderImage} alt="placeholder" />
          )}
          {!isEdit && (
            <Can I="create" a="campaign">
              <UploadButtonComponent
                id="campaignImage"
                helperText="400*400 - max 200kb"
                buttonText="Upload .png image"
                fileFormat={acceptableFormats.image}
                handleFileChange={handleCampaignImageChange}
                isDisable={isEdit}
              />
            </Can>
          )}
        </div>
      </FormSectionLayout>
    );
  };

  const handleChangeState = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    const value = event.target.value;
    switch (type) {
      case "title":
        setTitle(value);
        break;
      case "description":
        setDescription(value);
        break;
      case "recurrence":
        setRecurrence(value);
        updateEndDate(startDate, duration, value);
        break;
      case "duration":
        setDuration(value);
        updateEndDate(startDate, value, recurrence);
        break;
      case "status":
        if (status) {
          setStatus(false);
          setIsStatusChange(true);
        }
        break;
    }
  };

  const handleStartDateChange = (date: Date | [Date, Date] | null) => {
    if (date) {
      setStartDate(date.toString());
      updateEndDate(date.toString(), duration, recurrence);
    }
  };

  const handleBrandChange = (values: IMultiSelectOption[]) => {
    setBrands(values);
  };

  const handleTypeChange = (event: ISearchSelectChange) => {
    setType(event);
    if (event.value === CAMPAIGN_TYPE.BRAND_VALUE) {
      setCampaignImage(undefined);
      setBrands([]);
    }
  };

  const renderConfiguration = () => {
    return (
      <FormSectionLayout subTitleText="Campaign details">
        <SearchableSelect
          label="Campaign Type"
          options={CAMPAIGN_TYPE_DROPDOWN}
          id="campaign-type"
          onChange={handleTypeChange}
          selectedValue={type}
          placeholder="select a type"
          isSearchable={false}
          isDisabled={isEdit}
        />
        {type && type.value === CAMPAIGN_TYPE.BRAND_VALUE && (
          <MultiSelectDropDown
            options={productBrands}
            label="Brand"
            hidePlaceholder={isEdit}
            selectedValues={brands}
            placeholder={"Please select brands"}
            displayValue="label"
            onChange={handleBrandChange}
            onRemove={handleBrandChange}
            isDisable={isEdit}
          />
        )}

        <TextField
          id="title"
          label="title"
          placeholder="e.g. Dhamakedar campaign"
          value={title}
          handleChange={(e) => handleChangeState(e, "title")}
          disabled={isEdit}
        />

        <TextField
          id="description"
          label="description"
          placeholder="Khareedari par coins kamaein"
          value={description}
          type={"text"}
          handleChange={(e) => handleChangeState(e, "description")}
          disabled={isEdit}
        />

        <TextField
          id="duration"
          label="duration in days"
          placeholder="e.g. 7"
          value={duration}
          type={"number"}
          handleChange={(e) => handleChangeState(e, "duration")}
          disabled={isEdit}
        />

        <TextField
          id="recurrence"
          label="recurrence"
          placeholder="e.g. 4"
          value={recurrence}
          type={"number"}
          handleChange={(e) => handleChangeState(e, "recurrence")}
          disabled={isEdit}
        />

        {isEdit && (
          <Switch
            id="campaignStatus"
            label="Status"
            name="Status"
            checked={status}
            handleSwitch={(e) => {
              handleChangeState(e, "status");
            }}
            statusVal1="ACTIVE"
            statusVal2="INACTIVE"
          />
        )}
        <div className="form-row">
          <Col>
            <DatePicker
              selected={new Date(startDate)}
              dateFormat={DATE_FORMAT.ddMMyyyy}
              minDate={getTomorrowDate()}
              id="start-date"
              onChange={handleStartDateChange}
              disabled={isEdit}
              customInput={
                <TextField
                  id="startDate"
                  label="Start Date"
                  value={startDate}
                  placeholder={""}
                />
              }
            />
          </Col>
          <Col>
            <TextField
              id="endDate"
              label="End date"
              placeholder=""
              value={
                isEdit
                  ? formatDate(endDate.toString(), DATE_FORMAT.ddMMyyyy)
                  : endDate
              }
              disabled={true}
            />
          </Col>
        </div>
      </FormSectionLayout>
    );
  };
  return (
    <Container fluid>
      {renderNotificationModal()}
      {isLoading && <LoaderComponent />}
      <Row>
        <Col lg="5">
          <div className="back-button">
            <a href="#!" onClick={handleGoBackButton}>
              {/* TO-DO: Convert this to a button in all compoents. */}
              <span>
                <img src={IconBackArrow} alt="close" />
              </span>{" "}
              Back to Campaigns
            </a>
          </div>
          <HeadingText
            text={campaign ? `#${campaign.campaignNumber}` : "New Campaign"}
            isMainHeading={true}
          />
        </Col>
      </Row>

      <Row className="form-container">
        <Col xs="12">
          <Form>
            <div className="campaign-detail">
              {renderConfiguration()}
              {type &&
                type.value === CAMPAIGN_TYPE.BRAND_VALUE &&
                renderCampaignImage()}
              {!isEdit && renderCohort()}
              {renderSubmitButton()}
            </div>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default AddUpdateCampaignPage;
